/**
 * The live (unpersisted) half of the controlled transcript — composer turns, programmatic sends, a reattached
 * resume — held as state until seeded history owns them.
 *
 * Every write goes through here so three invariants hold in one place: a seeded id is never admitted as live, a
 * discarded id stays suppressed until a nav remounts ChatBox (whose next report is an unconditional replace), and a
 * settled reply's text equals the authoritative `final_message.text` (APP-3466).
 */

import { useCallback, useRef, useState } from "react"
import type { RefObject } from "react"
import type { ChatMessage } from "@mui/x-chat-headless"
import { settleFinalAnswerParts } from "./normalize-chunks"
import type { SkillProposal } from "./skill-proposal-contract"
import { useLatest } from "./use-latest"

const EMPTY_MESSAGES: ChatMessage[] = []
const EMPTY_SKILL_PROPOSALS: Record<string, SkillProposal[]> = {}

export interface LiveMessagesController {
  liveMessages: ChatMessage[]
  /** Read at nav-away / post-await, where the render closure is stale. */
  liveMessagesRef: RefObject<ChatMessage[]>
  /** ChatBox's report: whatever it holds beyond seeded history, minus anything discarded. */
  onMessagesChange: (next: ChatMessage[]) => void
  appendLiveMessage: (message: ChatMessage) => void
  /** Append only while `anchorId` survives — an uncommitted settle resets first, and appending after it strands an orphan bubble. */
  appendReplyTo: (anchorId: string, message: ChatMessage) => void
  /**
   * Replace the same-key entry in place, else append — resume calls this per frame, so a reattached turn renders
   * token-by-token as ONE bubble. Key on the caller's stable id, not `message.id`, which flips at finish.
   */
  upsertLiveMessage: (key: string, message: ChatMessage) => void
  /** Drop a live bubble for good; its id is suppressed until a nav remounts ChatBox. */
  discardLiveMessage: (messageId: string) => void
  /** Flag a live turn as failed (the affordance the live-send path raises on a rejected turn). */
  markLiveMessageErrored: (messageId: string) => void
  /** Roll back the whole transcript, suppressing every id in it (the uncommitted-settle reset). */
  discardAllLiveMessages: () => void
  /** Clear the transcript WITHOUT touching the suppression set (the render-phase deleted-thread reset). */
  clearLiveMessages: () => void
  /** Nav reset: ChatBox remounts on the new navKey and can no longer report the discarded ids, so stop suppressing. */
  resetLiveMessagesForNavigation: () => void
  /** Surface attach: stop suppressing WITHOUT clearing the transcript — a coexisting surface may still be streaming into it. */
  clearDiscardedLiveIds: () => void
  /** Hold a finished turn's canonical `final_message.text` until the report that reveals which reply settled. */
  parkFinalAnswer: (text: string) => void
  /** Drop only the parked text — a turn whose reply never settled must not hand its answer to the next turn. */
  discardParkedFinalAnswer: () => void
  /** Drop every parked and filed answer — a new view's replies must not inherit them. */
  resetFinalAnswers: () => void
  /**
   */
  skillProposalsByMessageId: Record<string, SkillProposal[]>
  parkSkillProposals: (proposals: SkillProposal[]) => void
  /**
   */
  claimParkedSkillProposals: (messageId: string) => void
  discardParkedSkillProposals: () => void
  resetSkillProposals: () => void
}

/** `seededIdsRef` is a param: a message the seeded half already owns must never be admitted as live. */
export function useLiveMessages(seededIdsRef: RefObject<ReadonlySet<string>>): LiveMessagesController {
  const [liveMessages, setLiveMessages] = useState<ChatMessage[]>(EMPTY_MESSAGES)
  // Ids of an aborted turn rolled back by an uncommitted settle.
  const discardedLiveIdsRef = useRef<ReadonlySet<string>>(new Set())
  const liveMessagesRef = useLatest(liveMessages)

  const finalAnswerByMessageIdRef = useRef<Map<string, string>>(new Map())
  const pendingFinalAnswerRef = useRef<string | null>(null)
  const [skillProposalsByMessageId, setSkillProposalsByMessageId] = useState<Record<string, SkillProposal[]>>(EMPTY_SKILL_PROPOSALS)
  const pendingSkillProposalsRef = useRef<SkillProposal[] | null>(null)

  // Matched by id, so a seeded-prefix change can't re-classify history as live.
  const onMessagesChange = useCallback((next: ChatMessage[]) => {
    const finalAnswerByMessageId = finalAnswerByMessageIdRef.current
    const newestReplyIndex = next.findLastIndex((message) => message.role === "assistant")
    const settledReplyId = newestReplyIndex >= 0 && next[newestReplyIndex].status === "sent"
      ? next[newestReplyIndex].id
      : null
    if (pendingFinalAnswerRef.current != null && settledReplyId != null) {
      finalAnswerByMessageId.set(settledReplyId, pendingFinalAnswerRef.current)
      pendingFinalAnswerRef.current = null
    }
    if (pendingSkillProposalsRef.current != null && settledReplyId != null) {
      const claimed = pendingSkillProposalsRef.current
      pendingSkillProposalsRef.current = null
      setSkillProposalsByMessageId((current) => ({ ...current, [settledReplyId]: claimed }))
    }
    setLiveMessages(next.reduce<ChatMessage[]>((live, message) => {
      if (seededIdsRef.current.has(message.id) || discardedLiveIdsRef.current.has(message.id)) return live
      const finalText = finalAnswerByMessageId.get(message.id)
      const parts = finalText == null ? message.parts : settleFinalAnswerParts(message.parts, finalText)
      live.push(parts === message.parts ? message : { ...message, parts })
      return live
    }, []))
  }, [seededIdsRef])

  const parkFinalAnswer = useCallback((text: string) => {
    pendingFinalAnswerRef.current = text
  }, [])

  const discardParkedFinalAnswer = useCallback(() => {
    pendingFinalAnswerRef.current = null
  }, [])

  const parkSkillProposals = useCallback((proposals: SkillProposal[]) => {
    if (proposals.length === 0) return
    pendingSkillProposalsRef.current = proposals
  }, [])

  const claimParkedSkillProposals = useCallback((messageId: string) => {
    const claimed = pendingSkillProposalsRef.current
    if (claimed == null) return
    pendingSkillProposalsRef.current = null
    setSkillProposalsByMessageId((current) => ({ ...current, [messageId]: claimed }))
  }, [])

  const discardParkedSkillProposals = useCallback(() => {
    pendingSkillProposalsRef.current = null
  }, [])

  const resetSkillProposals = useCallback(() => {
    pendingSkillProposalsRef.current = null
    setSkillProposalsByMessageId((current) => (current === EMPTY_SKILL_PROPOSALS ? current : EMPTY_SKILL_PROPOSALS))
  }, [])

  const resetFinalAnswers = useCallback(() => {
    finalAnswerByMessageIdRef.current = new Map()
    pendingFinalAnswerRef.current = null
  }, [])

  const appendLiveMessage = useCallback((message: ChatMessage) => {
    setLiveMessages((current) => [...current, message])
  }, [])

  const appendReplyTo = useCallback((anchorId: string, message: ChatMessage) => {
    setLiveMessages((current) => (current.some((existing) => existing.id === anchorId) ? [...current, message] : current))
  }, [])

  const upsertLiveMessage = useCallback((key: string, message: ChatMessage) => {
    setLiveMessages((current) => {
      if (seededIdsRef.current.has(key)) return current
      const index = current.findIndex((existing) => existing.id === key)
      if (index < 0) return [...current, message]
      if (current[index] === message) return current
      const next = current.slice()
      next[index] = message
      return next
    })
  }, [seededIdsRef])

  const discardLiveMessage = useCallback((messageId: string) => {
    discardedLiveIdsRef.current = new Set(discardedLiveIdsRef.current).add(messageId)
    setLiveMessages((current) => (current.some((message) => message.id === messageId)
      ? current.filter((message) => message.id !== messageId)
      : current))
  }, [])

  const markLiveMessageErrored = useCallback((messageId: string) => {
    setLiveMessages((current) => current.map((message) =>
      (message.id === messageId ? { ...message, status: "error" as const } : message)))
  }, [])

  const discardAllLiveMessages = useCallback(() => {
    discardedLiveIdsRef.current = new Set(liveMessagesRef.current.map((message) => message.id))
    setLiveMessages(EMPTY_MESSAGES)
  }, [liveMessagesRef])

  const clearLiveMessages = useCallback(() => {
    setLiveMessages(EMPTY_MESSAGES)
  }, [])

  const resetLiveMessagesForNavigation = useCallback(() => {
    discardedLiveIdsRef.current = new Set()
    setLiveMessages(EMPTY_MESSAGES)
  }, [])

  const clearDiscardedLiveIds = useCallback(() => {
    discardedLiveIdsRef.current = new Set()
  }, [])

  return {
    liveMessages,
    liveMessagesRef,
    onMessagesChange,
    appendLiveMessage,
    appendReplyTo,
    upsertLiveMessage,
    discardLiveMessage,
    markLiveMessageErrored,
    discardAllLiveMessages,
    clearLiveMessages,
    resetLiveMessagesForNavigation,
    clearDiscardedLiveIds,
    parkFinalAnswer,
    discardParkedFinalAnswer,
    resetFinalAnswers,
    skillProposalsByMessageId,
    parkSkillProposals,
    claimParkedSkillProposals,
    discardParkedSkillProposals,
    resetSkillProposals,
  }
}
